import {
  AnnotationRead,
  ClaimAnnotationResponse,
  ClaimCategorySchema,
  DataSourceEnum,
  FindingSelectorLabel,
  FindingTexterLabel,
  FindingTogglerLabel,
  FindingSchema as RemoteFinding,
  JobStudyRead,
  SelectorAttributes,
} from '@lunit-io/ctl-data-server';
import { nanoid } from 'nanoid';

import { dataApi } from 'src/http';
import { ClientError, ClientErrorCode } from 'src/http/client-error';
import {
  Claim,
  RemoteJob,
  LocalFinding,
  Label,
  ListJobResponse,
  Asset,
  SelectorAsset,
  LocalJob,
  getDefaultJob,
  CaseAPIResponse,
} from 'src/interfaces';
import { IssueReadSchema } from 'src/interfaces/issue';
import { shouldUseV3 } from 'src/utils/apiVersion';
import { getViewerTypeOrDefault } from 'src/utils/claim';
import FindingUtils from 'src/utils/finding';
import { ensure } from 'src/utils/typeHelper';

type AnnotationAPIResponse = Pick<
  ClaimAnnotationResponse,
  'findings' | 'labels'
>;

type JobProps = {
  claim: Claim;
  jobIndex?: number; // do we need it
  jobId: string;
  projectId: string;
  dataSource?: DataSourceEnum;
};

type GetJobCaseProps = {
  jobId: string;
  projectId: string;
  dataSource?: DataSourceEnum;
};

type GetJobAnnotationProps = {
  jobId: string;
  projectId: string;
  dataSource?: DataSourceEnum;
  claim: Claim;
};

/**
 * Transform v3 JobStudyRead response to CaseAPIResponse format
 */
const transformJobStudyToCaseResponse = (
  study: JobStudyRead
): CaseAPIResponse => {
  return {
    // Type assertion needed due to v3 API having slightly different image type definitions
    // The runtime data is compatible
    images: study.images as CaseAPIResponse['images'],
    report: study.report,
    // Type assertion needed due to v3 Finding type having optional fields vs required in FindingSchema
    findings: study.findings as CaseAPIResponse['findings'],
    labels: study.decisionLabels,
    patientId: study.patient,
    studyDate: '',
  };
};

/**
 * Get case in a certain job
 *
 * @param jobId string
 * @param projectId string
 * @param dataSource DataSource
 * @returns case CaseAPIResponse
 */
const getJobCase = async ({
  jobId,
  projectId,
  dataSource,
}: GetJobCaseProps): Promise<CaseAPIResponse> => {
  if (shouldUseV3(dataSource)) {
    // v3 API: GET /v3/projects/{project_id}/jobs/{id}/study
    const { data } = await dataApi.get<JobStudyRead>(
      `v3/projects/${projectId}/jobs/${jobId}/study`
    );
    return transformJobStudyToCaseResponse(data);
  }

  // v2 API: GET /jobs/{id}/case/
  const { data } = await dataApi.get<CaseAPIResponse>(`jobs/${jobId}/case/`);
  return data;
};

/**
 * Get annotation in a certain job
 *
 * @param jobId string
 * @param projectId string
 * @param dataSource DataSource
 * @param claim Claim
 * @returns annotation AnnotationAPIResponse
 */
const getJobAnnotation = async ({
  jobId,
  projectId,
  dataSource,
  claim,
}: GetJobAnnotationProps): Promise<AnnotationAPIResponse> => {
  let data: AnnotationAPIResponse;

  if (shouldUseV3(dataSource)) {
    // v3 API: GET /v3/projects/{project_id}/jobs/{id}/annotation
    const { data: v3Data } = await dataApi.get<AnnotationRead>(
      `v3/projects/${projectId}/jobs/${jobId}/annotation`
    );
    // Type assertion: v3 Finding type has optional fields, but runtime data is compatible
    data = v3Data as AnnotationAPIResponse;
  } else {
    // v2 API: GET /jobs/{id}/annotation/
    ({ data } = await dataApi.get<AnnotationAPIResponse>(
      `jobs/${jobId}/annotation/`
    ));
  }

  const savedFindings = data?.findings;
  if (!savedFindings) return data;

  /**
   * This adds default labels to saved findings to allow proper label syncing. It's a temporary solution
   * until we start using the ontology system. For background, see below or RAD-4654:
   * When BE returns saved findings, they don't include ALL the possible labels. They only include
   * the labels that were saved by the annotator. This causes issues when syncing findings
   * because syncing requires both findings to have labels with matching names.
   */
  const savedFindingsWithDefaultLabelsAdded = savedFindings.map(finding => {
    const defaultLabels = FindingUtils.getDefaultLabels(
      finding.shape,
      claim.assets
    );

    const updatedLabels = defaultLabels.map(defaultLabel => {
      if (finding.labels === undefined || finding.labels === null)
        return defaultLabel;

      const savedLabel = finding.labels.find(
        ({ name }) => name === defaultLabel.name
      );
      return savedLabel || defaultLabel;
    });
    return { ...finding, labels: updatedLabels };
  });
  return { ...data, findings: savedFindingsWithDefaultLabelsAdded };
};

/**
 *
 * @param assets (of claim)
 * @param label (can be annotation label or prediction label)
 * @returns alias for a selector finding (polygon)
 */
export const getAliasForFinding = (
  assets: Asset[],
  label: FindingTogglerLabel | FindingTexterLabel | FindingSelectorLabel
): string => {
  // !info: make sure label always exist
  if (!label) {
    return '';
  }

  const selectorAssets = assets.filter(
    asset =>
      !!((asset as SelectorAsset).formAttributes as SelectorAttributes)
        ?.categories
  ) as SelectorAsset[];

  const categories: ClaimCategorySchema[] =
    selectorAssets.flatMap<ClaimCategorySchema>(
      ({ formAttributes }) => (formAttributes as SelectorAttributes).categories
    );

  if (!categories.length) return '';

  const [findingType] = Object.keys(label.value).filter(
    type => typeof label.value === 'object' && label.value[type] === true
  );

  const finding = categories.find(
    cat => cat.name === findingType
  ) as ClaimCategorySchema;

  return finding?.alias || '';
};

/**
 * Add additional fields and values in the LocalFinding to the RemoteFinding
 */
const setLocalFindingFields =
  (claim: Claim) =>
  (finding: RemoteFinding, i: number): LocalFinding => {
    const nextIndex = i + 1;
    const { assets } = claim;
    const label = finding.labels?.[0];

    return {
      ...finding,
      index: nextIndex,
      group: !!finding.group ? finding.group : nanoid(5),
      alias: !!label ? getAliasForFinding(assets, label) : '',
    };
  };

interface EnqueueJobProps {
  projectId: string;
  dataSource?: DataSourceEnum;
  /**
   * Call focus API after enqueue. This is useful when annotator start the initial project,
   * which means no assigned job at all. Basically, setFocus determines whether to set or
   * not `currentJobId` on the project (most of the time it is true)
   */
  setFocus?: boolean;
}

/**
 * happens in multiple scenarios:
 *  - when user hits the last completed job and tries to get next job (if he still has assigned jobs)
 *  - when user has assigned jobs and he accesses the first time
 *
 * @returns Last Enqueued Job
 */
export const enqueueSingleJob = async ({
  projectId,
  dataSource,
  setFocus = false,
}: EnqueueJobProps): Promise<RemoteJob> => {
  if (shouldUseV3(dataSource)) {
    // v3 API: POST, returns single job directly
    const response = await dataApi.post<RemoteJob>(
      `v3/projects/${projectId}/jobs:enqueue`
    );

    if (!response || response.data === null) {
      throw new ClientError({ code: ClientErrorCode.NO_JOBS_TO_ENQUEUE });
    }

    return response.data;
  }

  // v2 API: PATCH, returns { success: [job] }
  const response = await dataApi.patch<ListJobResponse>(
    `projects/${projectId}/jobs/enqueue/`,
    { count: 1 },
    { params: { set_focus: setFocus } }
  );

  if (!response || response.data === null) {
    throw new ClientError({ code: ClientErrorCode.NO_JOBS_TO_ENQUEUE });
  }

  if (!response.data.success || response.data.success.length === 0) {
    throw new ClientError({ code: ClientErrorCode.NO_JOBS_TO_ENQUEUE });
  }

  return response.data.success[0]!;
};

/**
 * Get Job
 *
 * @returns getJob Function
 */
export const getJob = async ({
  claim,
  jobId,
  projectId,
  dataSource,
}: JobProps): Promise<LocalJob> => {
  // TODO: fix get return type as T | null
  const { data: apiJob } = await dataApi.get<RemoteJob>(`jobs/${jobId}/`);

  if (!apiJob) {
    throw new ClientError({
      code: ClientErrorCode.INVALID_JOB,
      message: `No job has been found with given ID : ${jobId}`,
    });
  }

  // Step 2: If it is reported, throw error for not to request more.
  if (apiJob.reported) {
    return {
      //TODO: Deprecate claim.viewer.type
      ...getDefaultJob({ type: getViewerTypeOrDefault(claim), images: [] }, []),
      reported: apiJob.reported,
    };
  }

  // Step 3: Get detail information of the job
  const [caseResponse, annotationResponse] = await Promise.all([
    getJobCase({ jobId: apiJob.id, projectId, dataSource }),
    getJobAnnotation({ jobId: apiJob.id, projectId, dataSource, claim }),
  ]);

  // TODO: add explanation when this happens under which circumstances
  claim.modes.forEach(mode => {
    if (!caseResponse.findings[mode.name]) {
      throw new ClientError({
        code: ClientErrorCode.INVALID_CASE,
        message:
          'Incomplete case response given in labels. Not all modes are filled.',
      });
    }
  });

  // Step 4: Fetch server's labels response to client's labels
  // modes: [annotation, prediction]
  const labels = claim.modes.reduce((acc: Record<string, Label[]>, mode) => {
    acc[mode.name] =
      mode.isEditable && !!annotationResponse
        ? annotationResponse.labels
        : ensure(caseResponse.labels[mode.name]);
    return acc;
  }, {});
  // final result: { annotation: [...], prediction: [...], }

  // Step 5: Fetch server's findings response to client's findings
  const findings = claim.modes.reduce(
    (acc: { [k: string]: LocalFinding[] }, mode) => {
      acc[mode.name] = (
        mode.isEditable && !!annotationResponse
          ? annotationResponse.findings.map(setLocalFindingFields(claim))
          : ensure(caseResponse.findings[mode.name]).map(
              setLocalFindingFields(claim)
            )
      ).map(FindingUtils.addConfirmedField);
      return acc;
    },
    {}
  );

  // in case of cpc project: this can be prior job if accessed on `/prior` route
  return {
    id: apiJob.id,
    images: caseResponse.images,
    metadata: caseResponse.metadata,
    findings,
    labels,
    report: caseResponse.report,
    annotationExists: !!annotationResponse,
    reported: apiJob.reported,
    patientId: caseResponse.patientId,
    studyDate: caseResponse.studyDate,
  };
};

export type JobListItem = {
  id: string;
  reported: boolean;
  pairJob?: string;
  pairType?: 'PairIndex' | 'PairPrior' | 'Normal';
  reading?: string;
  flagged?: boolean;
  findingCounts?: {
    box: number;
    point: number;
    line: number;
    polygon: number;
    multiFramePolygon: number;
  };
  issues?: IssueReadSchema[];
  completed: boolean;
};

export type PairedJobListItem = {
  index: JobListItem;
  prior: JobListItem | null; // exist only for CPC jobs
};
interface RemotePairedJobs {
  jobs: PairedJobListItem[];
}
interface RemoteJobs {
  jobs: JobListItem[];
}

export type GetAllJobIdsByProjectProps = {
  projectId: string;
  annotatorId: string;
};

const commonParamsOfJobList = {
  page_size: 1000000, // yee we need to provide this workaround due to BE request
  page: 1,
  fields: [
    'id',
    'reported',
    'reading',
    'flagged',
    'findingCounts',
    'issues',
    'completed',
  ],
  states: ['queued', 'completed', 'reported'],
  sort: ['+queuedAt', '+createdAt'],
};

export const getPairedJobList = async ({
  projectId,
  annotatorId,
}: GetAllJobIdsByProjectProps): Promise<PairedJobListItem[]> => {
  const response = await dataApi.get<RemotePairedJobs>(
    `/v2/projects/${projectId}/pair-jobs/`,
    {
      params: {
        ...commonParamsOfJobList,
        annotator: annotatorId,
      },
    }
  );

  return response?.data.jobs;
};

export const getJobList = async ({
  projectId,
  annotatorId,
}: GetAllJobIdsByProjectProps): Promise<JobListItem[]> => {
  const response = await dataApi.get<RemoteJobs>(
    `/v2/projects/${projectId}/jobs/`,
    {
      params: {
        ...commonParamsOfJobList,
        annotator: annotatorId,
      },
    }
  );

  return response?.data.jobs;
};

export const changeJobFlag = async ({
  jobId,
  flag,
}: {
  jobId?: string;
  flag: boolean;
}): Promise<void> => {
  if (!jobId) return;
  if (flag) {
    await dataApi.put(`jobs/${jobId}/flag`, {});
  } else {
    await dataApi.delete(`jobs/${jobId}/flag`);
  }
};
